import { Injectable, isDevMode } from "@angular/core";
import { MatomoTracker } from "@ngx-matomo/tracker";
import { AppSettings } from "../shared/Constants/sharedConstants";

export enum LogLevel {
  All = 0,
  Debug = 1,
  Info = 2,
  Warn = 3,
  Error = 4,
  Fatal = 5,
  EventTracking = 6,
}

const logColorStyle = "color: teal;";
const logBackgroundColorStyle = "background: transparent;";
const eventTrackingColorStyle = "color: white;";
const eventTrackingBackgroundColorStyle = "background: blue;";

@Injectable()
export class LogService {
  level: LogLevel = LogLevel.All;
  logWithDate: boolean = false;

  constructor(private matomoTracker: MatomoTracker) {}

  public debug(msg: string, ...optionalParams: any) {
    this.writeToLog(msg, LogLevel.Debug, optionalParams);
  }

  public info(msg: string, ...optionalParams: any) {
    this.writeToLog(msg, LogLevel.Info, optionalParams);
  }

  public warn(msg: string, ...optionalParams: any) {
    this.writeToLog(msg, LogLevel.Warn, optionalParams);
  }

  public error(msg: string, ...optionalParams: any) {
    this.writeToLog(msg, LogLevel.Error, optionalParams);
  }

  public fatal(msg: string, ...optionalParams: any) {
    this.writeToLog(msg, LogLevel.Fatal, optionalParams);
  }

  public log(msg: any, ...optionalParams: any) {
    this.writeToLog(msg, LogLevel.All, optionalParams);
  }

  private writeToLog(
    msg: any,
    logLevel: LogLevel = this.level,
    optionalParams: any
  ) {

    //convirm teh optionalParams into an array.
    if (Object.prototype.toString.call(optionalParams) !== "[object Array]") {
      optionalParams = [optionalParams];
    }

    //if the passed in msg is not a string then create a log string message
    // and add the msg object to the collection of optionalParms.
    const tof = typeof msg;
    if (tof === "string") {
      msg =
        "%c " +
        (this.logWithDate ? new Date().toString() : "") +
        msg;
    } else {
      optionalParams.push(msg);
      msg = "%c " + (this.logWithDate ? new Date().toString() : "");
    }

    switch (logLevel) {
      case LogLevel.Warn:
        console.warn(msg, optionalParams);
        break;
      case LogLevel.Error:
        console.error(msg, optionalParams);
        break;
      case LogLevel.Debug:
        if (!isDevMode()) {
          return;
        }
        console.debug(msg, optionalParams);
        break;
      case LogLevel.EventTracking:
        if (!isDevMode()) {
          return;
        }

        console.log(
          msg,
          eventTrackingBackgroundColorStyle + eventTrackingColorStyle,
          optionalParams
        );
        break;
      case LogLevel.All:
      case LogLevel.Info:
      default: {
        if (!isDevMode()) {
          return;
        }

        console.log(
          msg,
          logBackgroundColorStyle + logColorStyle,
          optionalParams
        );
      }
    }
  }

  // site tracker services (Matomo)
  public enableHeartBeatTimer(interval: number) {
    if (!this.logToSiteUsageTracker())
      this.writeToLog(
        "Matomo Heartbeat Timer enabled: ",
        LogLevel.EventTracking,
        interval
      );
    else this.matomoTracker.enableHeartBeatTimer(interval);
  }

  public enableLinkTracking(tf: boolean) {
    if (!this.logToSiteUsageTracker())
      this.writeToLog("Matomo Link Tracking set: ", LogLevel.EventTracking, tf);
    else this.matomoTracker.enableLinkTracking(tf);
  }

  public setUserId(id: string) {
    if (!this.logToSiteUsageTracker())
      this.writeToLog("Matomo User Id set: ", LogLevel.EventTracking, id);
    else this.matomoTracker.setUserId(id);
  }

  public setCustomVariable(
    id: number,
    name: string,
    value: string,
    type: any
  ) {
    if (!this.logToSiteUsageTracker())
      this.writeToLog(
        "Matomo Custom Variable set: ",
        LogLevel.EventTracking,
        "${id} ${name} ${value} ${type}"
      );
    else this.matomoTracker.setCustomVariable(id, name, value, type);
  }

  public trackEvent(category: string, action: string, name?: any, value: number | null = null) {
    if (!this.logToSiteUsageTracker())
      this.writeToLog(
        "Matomo Event Tracked: ",
        LogLevel.EventTracking,
        `${category} ${action} ${name} ${value}`
      );
    else this.matomoTracker.trackEvent(category, action, name, value);
  }

  public trackPageView(pageName: string) {
    if (!this.logToSiteUsageTracker())
      this.writeToLog(
        "Matomo Page View Tracked:",
        LogLevel.EventTracking,
        pageName
      );
    else {
      this.matomoTracker.trackPageView(pageName);
    }
  }

  logToSiteUsageTracker(): boolean {
    return AppSettings?.UIAppConfig.logToSiteUsageTracker;
  }
}
